import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { scanStore } from "../lib/store";

const AGE_OPTIONS = [
  "Less than 1 year ago",
  "1 to 3 years ago",
  "3 to 5 years ago",
  "More than 5 years ago",
  "I am not sure",
];
const CONDITION_OPTIONS = ["New", "Like New", "Good", "Fair", "Poor"];
const PACKAGING_OPTIONS = ["Yes", "No", "Not applicable"];

export default function Scan() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [photo, setPhoto] = useState(null); // dataUrl
  const [step, setStep] = useState("capture"); // capture | identifying | identified | questions | analyzing
  const [itemName, setItemName] = useState("");
  const [category, setCategory] = useState("");
  const [origPrice, setOrigPrice] = useState("");
  const [age, setAge] = useState("");
  const [condition, setCondition] = useState("");
  const [packaging, setPackaging] = useState("");
  const [notes, setNotes] = useState("");
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    api.get("/scans/status").then(({ data }) => setCredits(data)).catch(() => {});
  }, []);

  const openCamera = () => {
    fileInputRef.current?.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPhoto(dataUrl);
      setStep("identifying");
      identify(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const identify = async (dataUrl) => {
    try {
      const { data } = await api.post("/identify", { image_base64: dataUrl });
      setItemName(data.name || "Unknown Item");
      setCategory(data.category || "");
      setStep("identified");
    } catch (e) {
      toast.error("Couldn't identify item. Please type the name manually.");
      setItemName("");
      setStep("identified");
    }
  };

  const goQuestions = () => {
    if (!itemName.trim()) {
      toast.error("Please enter the item name.");
      return;
    }
    setStep("questions");
  };

  const analyze = async () => {
    if (!condition) {
      toast.error("Select a condition first.");
      return;
    }
    if (credits && credits.remaining <= 0) {
      navigate("/pricing");
      return;
    }
    setStep("analyzing");
    try {
      const { data } = await api.post("/analyze", {
        image_base64: photo,
        item_name_override: itemName,
        original_price: origPrice ? Number(origPrice) : null,
        purchase_age: age || null,
        condition,
        packaging: packaging || null,
        notes: notes || null,
      });
      scanStore.set({ currentAnalysis: data, currentPhoto: photo });
      navigate(`/results/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("No scans remaining.");
        navigate("/pricing");
      } else {
        toast.error(e.response?.data?.detail || "Analysis failed");
        setStep("questions");
      }
    }
  };

  return (
    <div className="px-6 pt-6 pb-8" data-testid="scan-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="camera-input"
      />

      {step === "capture" && (
        <div className="mm-card text-center" data-testid="capture-card">
          <div className="mm-label mb-3">Step 1</div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-3">
            Take a photo of your item
          </h2>
          <p className="text-slate-600 text-sm mb-6">
            Good light and a plain background give the best results.
          </p>
          <button
            className="mm-btn-primary"
            onClick={openCamera}
            data-testid="open-camera-btn"
          >
            <Camera size={22} strokeWidth={2.5} />
            Take a Photo
          </button>
        </div>
      )}

      {step === "identifying" && (
        <div className="mm-card flex items-center gap-3" data-testid="identifying">
          <Loader2 className="animate-spin text-blue-700" size={20} />
          <span className="font-medium">Analyzing your photo…</span>
        </div>
      )}

      {step === "identified" && (
        <div className="mm-card" data-testid="identified-card">
          <div className="mm-label mb-3">We found</div>
          <img
            src={photo}
            alt="Uploaded item"
            className="w-full max-h-64 object-cover rounded-2xl border border-slate-200 mb-4"
            data-testid="photo-preview"
          />
          <h2 className="text-2xl font-bold text-slate-900 mb-3">{itemName || "Unknown Item"}</h2>
          <label className="mm-label block mb-2">
            Not correct? Edit the item name
          </label>
          <input
            className="mm-input"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="e.g. Sony WH-1000XM5 Headphones"
            data-testid="item-name-input"
          />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              className="mm-btn-secondary"
              onClick={() => {
                setPhoto(null);
                setStep("capture");
              }}
              data-testid="retake-btn"
            >
              <RefreshCw size={16} />
              Retake
            </button>
            <button className="mm-btn-primary !h-14 !text-base" onClick={goQuestions} data-testid="continue-btn">
              Continue <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {step === "questions" && (
        <div data-testid="questions-card">
          <div className="mm-card mb-4">
            <div className="flex gap-3 items-center mb-4">
              <img src={photo} alt="item" className="w-14 h-14 rounded-2xl object-cover border border-slate-200" />
              <div>
                <div className="mm-label mb-1">Analyzing</div>
                <div className="font-bold text-slate-900">{itemName}</div>
              </div>
            </div>
          </div>

          <div className="mm-card mb-4">
            <label className="mm-label block mb-2">How much did you originally pay? (optional)</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-mono">$</span>
              <input
                className="mm-input pl-8"
                type="number"
                inputMode="decimal"
                min="0"
                value={origPrice}
                onChange={(e) => setOrigPrice(e.target.value)}
                placeholder="0.00"
                data-testid="original-price-input"
              />
            </div>
          </div>

          <Question label="When did you purchase it?" options={AGE_OPTIONS} value={age} onChange={setAge} testPrefix="age" />
          <Question label="What condition is it in?" options={CONDITION_OPTIONS} value={condition} onChange={setCondition} testPrefix="condition" />
          <Question label="Does it include the original packaging?" options={PACKAGING_OPTIONS} value={packaging} onChange={setPackaging} testPrefix="packaging" />

          <div className="mm-card mb-4">
            <label className="mm-label block mb-2">Anything else we should know?</label>
            <textarea
              rows={3}
              className="mm-input !h-auto py-3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Small scratch, missing charger, barely used, unopened, or any other important detail."
              data-testid="notes-input"
            />
          </div>

          <button className="mm-btn-primary" onClick={analyze} data-testid="estimate-btn">
            Estimate My Price
          </button>
        </div>
      )}

      {step === "analyzing" && (
        <div className="mm-card flex items-center gap-3" data-testid="analyzing">
          <Loader2 className="animate-spin text-blue-700" size={22} />
          <div>
            <div className="font-bold text-slate-900">Checking the market…</div>
            <div className="text-sm text-slate-500">
              Comparing against current retail and resale data
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Question({ label, options, value, onChange, testPrefix }) {
  return (
    <div className="mm-card mb-4">
      <label className="mm-label block mb-3">{label}</label>
      <div className="flex flex-col gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            className="mm-tile"
            data-active={value === opt}
            onClick={() => onChange(opt)}
            data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span>{opt}</span>
            {value === opt && <span className="text-blue-700 font-mono text-xs">SELECTED</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
